// Backend del dev-server Vite (:5173): SOLO in dev gli asset vogliono un URL
// assoluto (vedi ``apiAssetUrl``). In produzione la SPA è servita DAL backend
// stesso (``OM_WEB_STATIC_DIR``), quindi same-origin: un loopback lì dentro
// sarebbe irraggiungibile e bloccato come mixed content su https — è la
// «build nuda senza VITE_API_BASE_URL» che il 20/07 ha fatto sparire le foto.
const DEV_API_BASE = "http://127.0.0.1:8766";
// Annotazione esplicita: l'env Vite è ``any`` e qui serve una string vera
// (``isWebMode`` chiama ``startsWith``).
const BASE: string = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * True quando la SPA gira come web app pubblicata (build web:
 * ``$env:VITE_BASE="/"; $env:VITE_API_BASE_URL="https://om.bscom.it"``).
 *
 * Distinzione BUILD-TIME e non runtime (niente sniffing di ``omBridge``):
 * solo la build web parla con un backend che ha il gate di autenticazione
 * (401 senza sessione), mentre desktop Electron e dev server locale girano
 * SENZA gate. Un check runtime tipo "non c'è il bridge Electron" marcherebbe
 * come web anche il browser in dev contro il backend locale, mostrando una
 * UI di login che quel backend non richiede.
 */
export function isWebMode(): boolean {
  return BASE.startsWith("https://");
}

/**
 * Guardia 401 web-mode: calcola la destinazione del redirect alla pagina di
 * login, o ``null`` se non va fatto alcun redirect. Funzione pura (input
 * espliciti) per essere unit-testabile senza jsdom navigation. Anti-loop:
 * - solo in web-mode e solo su HTTP 401 (sul desktop il gate non esiste);
 * - mai per la chiamata di login stessa (l'errore va mostrato nel form);
 * - mai se siamo già sulla pagina /login.
 */
export function loginRedirectTarget(
  status: number,
  apiPath: string,
  currentPath: string,
  web: boolean = isWebMode(),
): string | null {
  if (!web || status !== 401) return null;
  if (apiPath.startsWith("/api/auth/login")) return null;
  if (currentPath === "/login" || currentPath.startsWith("/login?")) return null;
  // ``next`` = route di provenienza: la LoginPage ci rimanda lì dopo l'accesso.
  return `/login?next=${encodeURIComponent(currentPath)}`;
}

/**
 * Errore API con il codice del backend accanto al messaggio.
 *
 * Resta una ``Error`` con lo stesso ``message`` di prima — ``prettifyApiError``
 * e i ``catch`` esistenti non cambiano — ma porta anche ``code``, che serve a
 * distinguere i casi che non sono un fallimento: il login con secondo fattore
 * risponde ``mfa_required``, e il frontend deve chiedere il codice invece di
 * mostrare un errore rosso.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function raiseApiError(path: string, res: Response): Promise<never> {
  let detail = `${res.status} ${res.statusText}`;
  let code: string | null = null;
  try {
    const body = (await res.json()) as {
      error?: { message?: string; code?: string };
      detail?: string;
    };
    detail = body.error?.message ?? body.detail ?? detail;
    code = body.error?.code ?? null;
  } catch {
    // Keep the HTTP status fallback when the backend did not return JSON.
  }
  // Web-mode: 401 = sessione assente/scaduta su QUALSIASI endpoint → si
  // riparte dalla login. Qui è il punto meno invasivo: tutti i wrapper
  // ``api*`` passano da raiseApiError sulle risposte !ok, quindi la guardia
  // copre GET/POST/PUT/PATCH/DELETE senza toccare i singoli caller.
  // ``location.assign`` (full reload) azzera anche lo stato in-memory.
  const loginTarget = loginRedirectTarget(
    res.status,
    path,
    window.location.pathname + window.location.search,
  );
  if (loginTarget) window.location.assign(loginTarget);
  throw new ApiError(`[${path}] ${detail}`, res.status, code);
}

// Wrapper unico per fetch + annotazione path negli errori di rete.
// Senza questo le 3 pill "Failed to fetch" sullo stesso schermo sono
// indistinguibili (audit 2026-05-11 H5): il messaggio nativo del browser
// non include la URL, quindi non si capisce quale chiamata e' fallita.
async function fetchWithContext(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(`${BASE}${path}`, init);
  } catch (err) {
    // AbortError: il caller ha cancellato la richiesta (unmount, ricerca
    // re-typed). Propaga senza wrappare, cosi' il caller puo' fare il check
    // ``signal?.aborted`` standard.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    const message = err instanceof Error ? err.message : "errore di rete";
    throw new Error(`[${path}] ${message}`);
  }
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetchWithContext(path, init);
  if (!res.ok) await raiseApiError(path, res);
  return res.json() as Promise<T>;
}

// Corpo condiviso di POST/PUT/PATCH: cambia solo il verbo. Il body assente
// lascia i soli header del caller (niente Content-Type: json su una richiesta
// senza corpo).
async function apiSend<T>(
  method: "POST" | "PUT" | "PATCH",
  path: string,
  body?: unknown,
  init?: RequestInit,
): Promise<T> {
  const res = await fetchWithContext(path, {
    ...init,
    method,
    headers:
      body === undefined
        ? init?.headers
        : { ...(init?.headers ?? {}), "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) await raiseApiError(path, res);
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  return apiSend<T>("POST", path, body, init);
}

export async function apiPut<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  return apiSend<T>("PUT", path, body, init);
}

export async function apiPatch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  return apiSend<T>("PATCH", path, body, init);
}

export async function apiPostForm<T>(path: string, body: FormData, init?: RequestInit): Promise<T> {
  const res = await fetchWithContext(path, {
    ...init,
    method: "POST",
    body,
  });
  if (!res.ok) await raiseApiError(path, res);
  return res.json() as Promise<T>;
}

export async function apiDelete(path: string, init?: RequestInit): Promise<void> {
  const res = await fetchWithContext(path, {
    ...init,
    method: "DELETE",
  });
  if (!res.ok) await raiseApiError(path, res);
  // 204 No Content e' la norma per DELETE: niente body da parsare.
}

export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

/**
 * Costruisce l'URL di un asset caricato dal browser (``<img src>``,
 * ``<video>``, link a file). A differenza di ``apiUrl``, in DEV forza un URL
 * assoluto al backend invece del path relativo: Vite normalmente proxa
 * ``/api`` -> 8766, ma in alcuni contesti (es. img lazy-loaded fuori
 * viewport) il proxy puo' fare cose strane e l'asset non arriva al renderer
 * -> ``onError`` -> placeholder (audit 2026-05-19, Davide: "Mancano le foto"
 * pur con proxy 200 jpeg lato Python).
 *
 * Nelle build ``BASE`` è valorizzato da ``VITE_API_BASE_URL``; se mancasse,
 * il path resta RELATIVO same-origin (la SPA è servita dal backend stesso) —
 * mai un ``http://127.0.0.1`` che in https sarebbe mixed content bloccato.
 */
export function apiAssetUrl(path: string): string {
  return `${BASE || (import.meta.env.DEV ? DEV_API_BASE : "")}${path}`;
}

/** Filename dal Content-Disposition (``filename="..."``, con o senza virgolette).
 *  ``null`` se assente/illeggibile: in cross-origin (dev :5173 → :8766) l'header
 *  non e' esposto senza Access-Control-Expose-Headers, quindi il chiamante deve
 *  avere sempre un fallback. Vive qui (con i wrapper fetch) perché è un concern
 *  HTTP generico riusato da più feature (ordini export SDA, fatturazione XLSX). */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!m?.[1]) return null;
  const raw = m[1].trim();
  try {
    return decodeURIComponent(raw) || null;
  } catch {
    // Percent-encoding malformato: usa il valore grezzo (gia' leggibile).
    return raw || null;
  }
}

/**
 * Estrae un messaggio leggibile da qualsiasi errore lanciato dai wrapper
 * ``apiGet/apiPost/...``. Audit 2026-05-12 M10: prima i toast mostravano
 * raw ``[/api/orders/REF/generate-label] HTTP 500: ...``. Ora:
 * - rimuove il prefisso ``[/api/...]`` (utile in log, rumoroso in UI)
 * - tronca a 240 char per evitare toast giganti su stacktrace
 * - mappa errori comuni a frasi italiane piu' utente-friendly
 *
 * Esposto come utility cross-feature: ogni catch UI dovrebbe usarlo.
 */
export function prettifyApiError(err: unknown): string {
  let raw: string;
  if (err instanceof Error) {
    raw = err.message;
  } else if (typeof err === "string") {
    raw = err;
  } else if (err == null) {
    raw = "Errore sconosciuto";
  } else {
    // Tipi imprevisti (oggetti, numeri): JSON-stringify per evitare
    // ``[object Object]`` nei toast.
    try {
      raw = JSON.stringify(err);
    } catch {
      raw = "Errore sconosciuto";
    }
  }
  // Strip leading [/api/...] prefix se presente.
  const raw0 = raw.replace(/^\[\/[^\]]+\]\s*/, "").trim();
  const lower = raw0.toLowerCase();

  // Rete / infrastruttura (prima dei mapping di dominio).
  if (lower.includes("failed to fetch") || lower.includes("network error")) {
    return "Backend irraggiungibile. Riprova fra qualche secondo.";
  }
  if (lower.startsWith("503") || lower.includes("service unavailable")) {
    return "Servizio temporaneamente non disponibile. Riprova fra poco.";
  }
  if (lower.startsWith("504") || lower.includes("gateway timeout")) {
    return "Timeout della richiesta. Riprova.";
  }
  if (lower.startsWith("401") || lower.startsWith("403")) {
    return "Credenziali non valide o mancanti. Controlla le Impostazioni.";
  }
  // Corpo non-JSON (pagina d'errore del proxy, 413 di uvicorn, risposta
  // troncata): chi lancia lascia la sola status line INGLESE (``raiseApiError``
  // qui sopra e ``features/fatturazione/api.ts``) e nessuna regola sotto la
  // riconosce — nemmeno ``looksTechnical`` — quindi l'utente leggerebbe
  // «502 Bad Gateway». Il match è sull'INTERA stringa: un messaggio di dominio
  // che inizia per cifre («500 ordini elaborati») non dev'essere scambiato per
  // uno status. ``statusText`` è vuoto in HTTP/2 → gruppo opzionale.
  const statusNudo = /^([1-5]\d{2})(\s+[A-Z][A-Za-z' -]*)?$/.exec(raw0);
  if (statusNudo) {
    const codice = Number(statusNudo[1]);
    if (codice === 413) return "Il file è troppo grande: il server lo ha rifiutato.";
    if (codice === 429) return "Troppe richieste in poco tempo. Aspetta qualche secondo e riprova.";
    if (codice === 404) return "Risorsa non trovata sul server. Ricarica la pagina e riprova.";
    return codice >= 500
      ? `Il server non ha risposto correttamente (errore ${codice}). Riprova fra poco.`
      : `Richiesta rifiutata dal server (errore ${codice}). Controlla i dati e riprova.`;
  }

  // Audit 2026-05-19: il backend Poleepo emette messaggi gia' in
  // italiano comprensibili (vedi ``_format_poleepo_error``):
  // ``Poleepo ha rifiutato la richiesta (HTTP NNN). Corriere: ...``.
  // Pass-through diretto: non serve rimappare/wrappare con "(dettaglio:)".
  if (/^Poleepo ha (rifiutato|risposto)/.test(raw0)) {
    return raw0.length > 500 ? raw0.slice(0, 497) + "..." : raw0;
  }
  // Dettaglio tecnico ripulito (no JSON/newline rumorosi) per la coda
  // dei mapping generici. Cap a 200 char (era 90): mappature legacy che
  // arrivano qui invece del pass-through Poleepo (vedi sopra) non
  // devono perdere il pezzo finale del messaggio del corriere.
  // ``{}`` e ``"`` strippati: il fallback generico copre risposte 5xx
  // con corpo JSON grezzo (es. trace KeyError) — quelle quando arrivano
  // devono restare leggibili, non sembrare JSON dump.
  const tech = raw0.replace(/\s+/g, " ").replace(/[{}"]/g, "").trim().slice(0, 200);
  const withTech = (human: string): string =>
    tech && !human.toLowerCase().includes(tech.toLowerCase().slice(0, 24))
      ? `${human} (dettaglio: ${tech})`
      : human;

  // Mappa errori NOTI → frase italiana semplice (cosa è successo + cosa fare).
  // L'ordine conta: regole più specifiche prima.
  const rules: { re: RegExp; human: string }[] = [
    {
      re: /width is lesser than zero|(dimension|package).*(lesser than zero|negativ)/i,
      human:
        "Poleepo ha rifiutato la spedizione: dimensioni del pacco non valide " +
        "(manca larghezza/lunghezza/altezza). Controlla i colli dell'ordine o il listino fornitore.",
    },
    {
      re: /order is full[_ ]?shipped|full[_ ]?shipped/i,
      human: "L'ordine risulta già spedito su Poleepo: nessuna nuova LDV è stata creata.",
    },
    {
      re: /\berror["\s:]*1000\b|internal error/i,
      human:
        "Poleepo non è riuscito a creare la spedizione (errore interno del corriere). " +
        "Riprova; se persiste verifica corriere e dati del pacco.",
    },
    {
      re: /upload ftp cosma fallito|ftp.*cosma.*(fallit|non raggiung)|cosma.*ftp.*(fallit|non raggiung)/i,
      human:
        "Invio a Cosma fallito: server FTP non raggiungibile. " +
        "Controlla connessione e credenziali FTP Cosma nelle Impostazioni.",
    },
    {
      re: /non verificato sul server|upload .*non verificat/i,
      human:
        "Invio non confermato dal server: il file potrebbe non essere arrivato. " +
        "Riprova e verifica sull'FTP.",
    },
    {
      re: /partita iva cosma/i,
      human: "Configura la Partita IVA Cosma nelle Impostazioni → COSMA.",
    },
    {
      re: /credenziali.*(non configurat|mancant)|keyring|secret\b.*(not found|mancant)/i,
      human:
        "Credenziali mancanti o non configurate. Vai in Impostazioni e completa la configurazione.",
    },
    {
      re: /errore poleepo \d+|poleepo (post|get|put|\/).*fallit/i,
      human:
        "Poleepo ha rifiutato la richiesta. Controlla i dati dell'ordine; " +
        "se persiste segnala il dettaglio.",
    },
    {
      re: /nessun ordine.*(selezionat|valid)/i,
      human: "Nessun ordine valido per questa azione. Seleziona almeno un ordine idoneo.",
    },
    {
      re: /bot-status|bot-pause|human-takeover|brain/i,
      human: "Stato del bot WhatsApp non raggiungibile",
    },
    {
      re: /allegati non supportati su whatsapp/i,
      human: "Su WhatsApp puoi inviare solo testo (per ora)",
    },
  ];
  for (const r of rules) {
    if (r.re.test(raw0)) return withTech(r.human);
  }

  // Già in italiano chiaro (niente JSON/HTTP/stacktrace) → passthrough.
  const looksTechnical = /[{}]|"error"|http \d|traceback|exception|\bat \w+[.(]/i.test(raw0);
  if (!looksTechnical) {
    return raw0.length > 200 ? raw0.slice(0, 197) + "..." : raw0 || "Errore sconosciuto";
  }
  // Sconosciuto e tecnico → fallback pulito + dettaglio in coda.
  return withTech(
    "Operazione non riuscita. Riprova; se l'errore persiste segnala questo dettaglio.",
  );
}
